import { useMemo, useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteNav from "@/components/feature/SiteNav";
import SiteFooter from "@/components/feature/SiteFooter";
import { ArticleCard } from "@/components/feature/ArticleCard";
import { articles, categories } from "@/mocks/siteData";

export default function CategoryPage() {
  const { slug } = useParams();
  const [query, setQuery] = useState("");
  const cat = categories.find((c) => c.slug === slug);

  const list = useMemo(() => {
    const base = slug ? articles.filter((a) => a.categorySlug === slug) : articles;
    if (!query.trim()) return base;
    const q = query.trim().toLowerCase();
    return base.filter((a) => (a.title + a.excerpt + a.tags.join("")).toLowerCase().includes(q));
  }, [slug, query]);

  useEffect(() => {
    setQuery("");
  }, [slug]);

  useEffect(() => {
    if (cat) {
      document.title = `${cat.name} · ${cat.blurb.slice(0, 24)} - 猎奇网`;
    }
  }, [cat]);

  const heroTop = categories[0];
  const catCount = (id: string) => articles.filter((a) => a.categorySlug === id).length;

  if (!cat) {
    return (
      <div className="bg-background-50">
        <SiteNav />
        <main className="mx-auto flex min-h-[70vh] max-w-[1400px] flex-col items-center justify-center px-4 text-center">
          <p className="font-heading text-3xl font-black text-primary-700">404</p>
          <h1 className="mt-3 font-heading text-xl font-bold text-foreground-900">
            这个栏目尚未被发掘
          </h1>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true"></i>
            返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const featured = list[0];
  const rest = list.slice(1);

  const listLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${cat.name} - ${cat.en}`,
    description: cat.blurb,
    inLanguage: "zh-CN",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "猎奇网", item: "https://0p622.cn/" },
        { "@type": "ListItem", position: 2, name: cat.name, item: `https://0p622.cn/explore/${cat.slug}` },
      ],
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: list.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://0p622.cn/article/${a.slug}`,
        name: a.title,
      })),
    },
  };

  return (
    <div className="bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(listLd) }}
      />
      <SiteNav />

      {/* dark hero */}
      <section className="relative overflow-hidden bg-foreground-950 text-background-50">
        <img
          src={cat.cover}
          alt={`${cat.name}栏目 ${cat.en} 主题图`}
          className="absolute inset-0 h-full w-full object-cover object-top opacity-60"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-foreground-950/60 to-foreground-950"></div>
        <div className="relative mx-auto max-w-[1400px] px-4 pb-10 pt-28 md:px-6 md:pt-36">
          <nav aria-label="面包屑" className="mb-6">
            <ol className="flex items-center gap-1.5 text-xs text-background-100/75">
              <li>
                <Link to="/" className="transition-colors hover:text-primary-300">
                  首页
                </Link>
              </li>
              <li aria-hidden="true">
                <i className="ri-arrow-right-s-line"></i>
              </li>
              <li aria-current="page" className="text-primary-300">
                {cat.name}
              </li>
            </ol>
          </nav>
          <p className="text-xs font-semibold tracking-[0.22em] text-primary-300">{cat.en}</p>
          <h1 className="mt-2 font-heading text-4xl font-black md:text-5xl">{cat.name}</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-100/80 md:text-base">
            {cat.blurb}
          </p>
        </div>
      </section>

      <main className="mx-auto max-w-[1400px] px-4 py-12 md:px-6 md:py-16">
        {/* category tabs + search */}
        <div className="mb-10 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="hide-scrollbar flex items-center gap-2 overflow-x-auto">
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/explore/${c.slug}`}
                className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  c.slug === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
                }`}
              >
                {c.name}
                <span
                  className={`rounded-full px-1.5 text-xs ${
                    c.slug === cat.slug ? "bg-background-50/20" : "bg-secondary-200"
                  }`}
                >
                  {catCount(c.slug)}
                </span>
              </Link>
            ))}
          </div>
          <label className="relative block w-full lg:w-80">
            <span className="sr-only">搜索本栏目</span>
            <i
              className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`搜索${cat.name}内线索`}
              className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-10 pr-4 text-sm text-foreground-800 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
            />
          </label>
        </div>

        {list.length === 0 ? (
          <div className="rounded-xl border border-background-200 bg-background-100 py-20 text-center">
            <i className="ri-search-eye-line text-4xl text-foreground-400" aria-hidden="true"></i>
            <p className="mt-4 text-sm text-foreground-600">没有找到匹配的线索，换个关键词试试。</p>
          </div>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {featured && (
              <ArticleCard article={featured} className="sm:col-span-2 lg:col-span-1" />
            )}
            {rest.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        )}

        {/* note */}
        <div className="mt-14 flex flex-col items-center justify-between gap-4 rounded-xl border border-dashed border-background-300 bg-background-100 px-6 py-6 sm:flex-row">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-background-50">
              <i className="ri-map-pin-time-line" aria-hidden="true"></i>
            </span>
            <div>
              <p className="font-heading text-sm font-bold text-foreground-800">
                想按时间或来源浏览 {cat.name}？
              </p>
              <p className="text-xs text-foreground-500">
                更多{cat.name}内容正在陆续整理，欢迎关注其他主题栏目。
              </p>
            </div>
          </div>
          <Link
            to={`/explore/${heroTop.slug}`}
            className="inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            看看{heroTop.name}
          </Link>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}